// Lazy Loader Utility for Fortisys Website
// Handles lazy loading of images and content using Intersection Observer

export class LazyLoader {
  constructor(options = {}) {
    this.options = {
      rootMargin: '50px',
      threshold: 0.1,
      loadingClass: 'lazy-loading',
      loadedClass: 'lazy-loaded',
      errorClass: 'lazy-error',
      ...options
    };

    this.observer = null;
    this.images = new Set();
    this.init();
  }

  init() {
    // Check for Intersection Observer support
    if (!('IntersectionObserver' in window)) {
      this.loadAllImages();
      return;
    }

    this.createObserver();
    this.findAndObserveImages();
  }

  createObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: this.options.rootMargin,
      threshold: this.options.threshold
    });
  }

  findAndObserveImages() {
    // Find images with data-src attribute (lazy loading)
    const lazyImages = document.querySelectorAll('img[data-src]:not([data-lazy-loaded])');

    lazyImages.forEach(img => {
      this.observeImage(img);
    });

    // Find images with srcset for responsive loading
    const responsiveImages = document.querySelectorAll('img[data-srcset]:not([data-lazy-loaded])');

    responsiveImages.forEach(img => {
      this.observeImage(img);
    });

    // Find background images with lazy loading
    const lazyBackgrounds = document.querySelectorAll('[data-bg]:not([data-lazy-loaded])');

    lazyBackgrounds.forEach(element => {
      this.observeImage(element);
    });
  }

  observeImage(element) {
    if (!this.observer) return;

    this.images.add(element);
    this.observer.observe(element);

    // Add loading class
    element.classList.add(this.options.loadingClass);
  }

  loadImage(element) {
    return new Promise((resolve, reject) => {
      const isImg = element.tagName === 'IMG';
      const dataSrc = element.getAttribute('data-src');
      const dataSrcset = element.getAttribute('data-srcset');
      const dataBg = element.getAttribute('data-bg');

      if (isImg) {
        this.loadImageElement(element, dataSrc, dataSrcset)
          .then(() => resolve(element))
          .catch(() => reject(element));
      } else if (dataBg) {
        this.loadBackgroundImage(element, dataBg)
          .then(() => resolve(element))
          .catch(() => reject(element));
      } else {
        resolve(element);
      }
    });
  }

  loadImageElement(img, dataSrc, dataSrcset) {
    return new Promise((resolve, reject) => {
      const imageLoader = new Image();

      imageLoader.onload = () => {
        // Update the actual img element
        if (dataSrc) {
          img.src = dataSrc;
          img.removeAttribute('data-src');
        }

        if (dataSrcset) {
          img.srcset = dataSrcset;
          img.removeAttribute('data-srcset');
        }

        this.onImageLoaded(img);
        resolve(img);
      };

      imageLoader.onerror = () => {
        this.onImageError(img);
        reject(img);
      };

      // Start loading
      if (dataSrcset) {
        imageLoader.srcset = dataSrcset;
      }
      if (dataSrc) {
        imageLoader.src = dataSrc;
      }
    });
  }

  loadBackgroundImage(element, dataBg) {
    return new Promise((resolve, reject) => {
      const imageLoader = new Image();

      imageLoader.onload = () => {
        element.style.backgroundImage = `url(${dataBg})`;
        element.removeAttribute('data-bg');
        this.onImageLoaded(element);
        resolve(element);
      };

      imageLoader.onerror = () => {
        this.onImageError(element);
        reject(element);
      };

      imageLoader.src = dataBg;
    });
  }

  onImageLoaded(element) {
    element.classList.remove(this.options.loadingClass);
    element.classList.add(this.options.loadedClass);
    element.setAttribute('data-lazy-loaded', 'true');

    // Dispatch custom event
    const event = new CustomEvent('lazyImageLoaded', {
      detail: { element, loader: this }
    });

    element.dispatchEvent(event);
    this.images.delete(element);
  }

  onImageError(element) {
    element.classList.remove(this.options.loadingClass);
    element.classList.add(this.options.errorClass);

    // Dispatch custom event
    const event = new CustomEvent('lazyImageError', {
      detail: { element, loader: this }
    });

    element.dispatchEvent(event);
    this.images.delete(element);
  }

  // Load all images immediately (fallback for unsupported browsers)
  loadAllImages() {
    const allLazyImages = document.querySelectorAll('img[data-src], img[data-srcset], [data-bg]');

    allLazyImages.forEach(element => {
      this.loadImage(element);
    });
  }

  // Public API methods
  forceLoadImage(element) {
    if (this.observer) {
      this.observer.unobserve(element);
    }
    return this.loadImage(element);
  }

  refresh() {
    // Find and observe new images that may have been added to the DOM
    this.findAndObserveImages();
  }

  // Add new images to be lazy loaded
  observe(elements) {
    if (!Array.isArray(elements)) {
      elements = [elements];
    }

    elements.forEach(element => {
      this.observeImage(element);
    });
  }

  // Remove images from observation
  unobserve(elements) {
    if (!this.observer) return;

    if (!Array.isArray(elements)) {
      elements = [elements];
    }

    elements.forEach(element => {
      this.observer.unobserve(element);
      this.images.delete(element);
    });
  }

  // Get loading statistics
  getStats() {
    const allLazyImages = document.querySelectorAll('[data-lazy-loaded="true"]');
    const loadedImages = document.querySelectorAll(`.${this.options.loadedClass}`);
    const errorImages = document.querySelectorAll(`.${this.options.errorClass}`);

    return {
      total: this.images.size + allLazyImages.length,
      loaded: loadedImages.length,
      errors: errorImages.length,
      pending: this.images.size
    };
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.images.clear();

    // Remove loading classes
    const loadingImages = document.querySelectorAll(`.${this.options.loadingClass}`);
    loadingImages.forEach(img => {
      img.classList.remove(this.options.loadingClass);
    });
  }
}

// Utility function to convert regular images to lazy loading
export function convertToLazyLoading(container = document) {
  const images = container.querySelectorAll('img[src]:not([data-src])');

  images.forEach(img => {
    const src = img.getAttribute('src');
    const srcset = img.getAttribute('srcset');

    // Move src to data-src
    if (src) {
      img.setAttribute('data-src', src);
      img.removeAttribute('src');
    }

    // Move srcset to data-srcset
    if (srcset) {
      img.setAttribute('data-srcset', srcset);
      img.removeAttribute('srcset');
    }

    // Add a placeholder or low-quality image
    img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMSIgaGVpZ2h0PSIxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNjY2MiLz48L3N2Zz4=';
  });
}
